/*
 * erHelpers — pure helpers for the Event Readiness orchestration surface.
 * No network, no state. Reuses CATEGORY_LABELS / formatDueDate from
 * operationStates.js so labels stay consistent with the rest of the feature.
 */
import { CATEGORY_LABELS, formatDueDate } from './operationStates';

export { CATEGORY_LABELS, formatDueDate };

/* ---- source provenance: Manual · AI Suggested · AI Detected ---- */
export function sourceMeta(op) {
  // Derived-from-live-data takes precedence so "looks handled" reads AI Detected.
  if (op?.derived_status === 'done') return { key: 'detected', label: 'AI Detected' };
  const source = op?.source;
  const kind = op?.milo_kind;
  if (source === 'system_plan') return { key: 'suggested', label: 'AI Suggested' };
  if (source === 'milo' && kind === 'suggested') return { key: 'suggested', label: 'AI Suggested' };
  if (source === 'system_signal' || (source === 'milo' && kind === 'observation')) {
    return { key: 'detected', label: 'AI Detected' };
  }
  return { key: 'manual', label: 'Manual' };
}

export function isMiloSuggestion(op) {
  return op?.source === 'milo' && op?.milo_kind === 'suggested';
}

/* ---- time-of-day greeting (mirrors backend _time_of_day_phrase) ---- */
export function greetingWord(date = new Date()) {
  const h = date.getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Working late';
}

export function weekdayName(date = new Date()) {
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
}

/* ---- days until an ISO date (positive = future) ---- */
export function daysUntil(iso) {
  if (!iso) return null;
  try {
    const target = new Date(String(iso).slice(0, 10) + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today) / 86400000);
  } catch {
    return null;
  }
}

export function isOverdue(op, todayIso) {
  if (!op?.due_date) return false;
  if (op.state === 'completed' || op.state === 'snoozed') return false;
  // The backend can detect a task as already handled from live data
  // (derived_status === 'done') even before the host confirms it — e.g.
  // save-the-dates that the guest-journeys show as sent. Honor that here so a
  // "looks handled" card never carries a stale "Overdue" chip.
  if (op.derived_status === 'done') return false;
  return String(op.due_date).slice(0, 10) < todayIso;
}

/* ---- priority score for "Do next" ordering (higher = sooner) ---- */
const URGENCY_RANK = { today: 3, this_week: 2, this_month: 1, when_possible: 0 };
const STATE_RANK = { pending_decision: 3, blocked: 2, active: 1, follow_up: 1, scheduled: 0 };

export function priorityScore(op, todayIso) {
  let s = 0;
  if (isOverdue(op, todayIso)) s += 100;
  s += (URGENCY_RANK[op.urgency] ?? 1) * 10;
  s += STATE_RANK[op.state] ?? 1;
  const d = daysUntil(op.due_date);
  if (d != null) s += Math.max(0, 30 - d) / 10; // nearer due dates rank a touch higher
  if (isMiloSuggestion(op)) s += 1; // surface AI suggestions near the top of their tier
  return s;
}

/*
 * Partition operations into the surface's groups. Pure; order within groups
 * is the caller's concern except doNext which we pre-sort by priority.
 *
 *   handled  — derived-done (Phase 1) awaiting host confirmation
 *   doNext   — open & pressing (today/this_week/overdue/blocked/decisions/suggestions)
 *   comingUp — open but later (this_month/when_possible/scheduled/snoozed)
 *   waiting  — waiting on / delegated to others
 *   done     — completed
 */
export function groupOperations(operations) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const groups = { handled: [], doNext: [], comingUp: [], waiting: [], done: [] };

  for (const op of operations || []) {
    if (op.state === 'completed') { groups.done.push(op); continue; }
    // Host's explicit state wins over a derived hint: a snoozed ("not yet") or
    // waiting task is honored before the "looks handled" treatment.
    if (op.state === 'waiting' || op.state === 'delegated') { groups.waiting.push(op); continue; }
    if (op.state === 'snoozed') { groups.comingUp.push(op); continue; }
    // Phase 1: derived-done but host hasn't confirmed → "looks handled"
    if (op.derived_status === 'done' && !op.derived_confirmed) { groups.handled.push(op); continue; }

    const pressing =
      isOverdue(op, todayIso) ||
      op.state === 'pending_decision' ||
      op.state === 'blocked' ||
      isMiloSuggestion(op) ||
      op.urgency === 'today' ||
      op.urgency === 'this_week';

    (pressing ? groups.doNext : groups.comingUp).push(op);
  }

  groups.doNext.sort((a, b) => priorityScore(b, todayIso) - priorityScore(a, todayIso));
  groups.comingUp.sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'));
  // most-recently completed first
  groups.done.sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));
  return groups;
}

/* ---- deterministic T-minus timeline from the event date ---- */
const TL_MARKS = [
  { days: 120, label: '120 days', tip: 'Foundation — venue, date & budget.' },
  { days: 90, label: '90 days', tip: 'Lock key vendors — catering, photography.' },
  { days: 60, label: '60 days', tip: 'Send invitations · plan menu & details.' },
  { days: 30, label: '30 days', tip: 'Finalize headcount & seating.' },
  { days: 14, label: '14 days', tip: 'Confirmations & run-of-show.' },
  { days: 7, label: '7 days', tip: 'Final payments & vendor briefings.' },
  { days: 0, label: 'Day of', tip: 'Coordination & the celebration itself.' },
];

/* Map a "days out" value to a percent along the evenly-spaced 120→0 axis,
 * using the same bracket interpolation as the "now" dot so pins line up. */
function dayToPct(daysOut) {
  const n = TL_MARKS.length;
  if (daysOut >= TL_MARKS[0].days) return 0;
  if (daysOut <= 0) return 100;
  for (let i = 0; i < n - 1; i++) {
    const hi = TL_MARKS[i].days, lo = TL_MARKS[i + 1].days;
    if (daysOut <= hi && daysOut >= lo) {
      const frac = (hi - daysOut) / (hi - lo);
      return ((i + frac) / (n - 1)) * 100;
    }
  }
  return 0;
}

/*
 * Returns null when there's no event date. Otherwise a render-ready model:
 *   { daysOut, nowPct, fillPct, marks:[{label,pct,done,tip}], pins:[{title,date,daysOut,pct,kind}] }
 * Markers are spaced evenly (even though the day-gaps differ) so labels read
 * cleanly, matching the approved concept. The "now" dot is interpolated.
 *
 * `dated` overlays the host's OWN dated items (to-dos with a due date, captured
 * calendar entries) as pins — this is what turns the generic milestone strip
 * into "the road to YOUR event."
 */
export function buildTimeline(eventDateIso, dated = []) {
  const daysOut = daysUntil(eventDateIso);
  if (daysOut == null) return null;
  const n = TL_MARKS.length;
  const marks = TL_MARKS.map((m, i) => ({
    label: m.label,
    tip: m.tip,
    pct: (i / (n - 1)) * 100,
    done: daysOut <= m.days, // a milestone window has passed once we're inside it
    days: m.days,
  }));

  const nowPct = dayToPct(daysOut);

  const pins = (dated || [])
    .map((it) => {
      const d = daysUntil(it.date);
      return d == null ? null : { ...it, daysOut: d, pct: dayToPct(d) };
    })
    .filter((p) => p && p.daysOut >= 0)   // only items still ahead of us
    .sort((a, b) => b.daysOut - a.daysOut);

  return { daysOut, nowPct, fillPct: nowPct, marks, pins };
}

/* current window label, e.g. "120-to-90-day stretch" */
export function currentWindow(daysOut) {
  if (daysOut == null) return null;
  for (let i = 0; i < TL_MARKS.length - 1; i++) {
    const hi = TL_MARKS[i].days, lo = TL_MARKS[i + 1].days;
    if (daysOut <= hi && daysOut > lo) {
      return lo === 0 ? `final ${hi} days` : `${hi}-to-${lo}-day stretch`;
    }
  }
  if (daysOut > TL_MARKS[0].days) return 'early planning';
  return 'event week';
}
